import { makeAutoObservable } from "mobx";
import { CommissionApi } from "../../apis/Commission";
import { GetWithdrawsForMonthParams, GetWithdrawsForMonthResponse, Withdraw, AutoWithdrawJobStatus } from "../../models/Commission";

export class CommissionManagementPageModel {
    // 数据状态
    loading = false;
    data: GetWithdrawsForMonthResponse | null = null;
    autoWithdrawJobStatus: AutoWithdrawJobStatus | null = null;
    autoWithdrawJobLoading = false;
    
    // 筛选条件
    selectedMonth = "";
    searchKeyword = "";
    statusFilter = "";
    
    // 弹窗状态
    showConfirmDialog = false;
    confirmDialogTitle = "";
    confirmDialogContent = "";
    confirmDialogAction: (() => void) | null = null;
    
    // 提示状态
    showHint = false;
    hintMessage = "";
    
    constructor() {
        makeAutoObservable(this);
        this.initDefaultMonth();
        this.loadAutoWithdrawJobStatus();
    }
    
    // 初始化默认月份
    initDefaultMonth() {
        const now = new Date();
        const currentDay = now.getDate();
        
        if (currentDay <= 10) {
            // 当月10日之前（包含10日），显示当月
            this.selectedMonth = this.formatMonth(now);
        } else {
            // 当月10日之后，显示下个月
            const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
            this.selectedMonth = this.formatMonth(nextMonth);
        }
    }
    
    // 格式化月份为 YYYY-MM 格式
    formatMonth(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    }
    
    // 设置月份
    setSelectedMonth(month: string) {
        this.selectedMonth = month;
    }
    
    // 设置搜索关键词
    setSearchKeyword(keyword: string) {
        this.searchKeyword = keyword;
    }
    
    // 设置状态筛选
    setStatusFilter(status: string) {
        this.statusFilter = status;
    }
    
    // 查询数据
    async queryData() {
        if (!this.selectedMonth) return;
        
        this.loading = true;
        try {
            const params: GetWithdrawsForMonthParams = {
                month: this.selectedMonth
            };
            
            // 如果有搜索关键词，添加到参数中
            if (this.searchKeyword.trim()) {
                // 判断是手机号还是卖家ID
                if (/^\d{11}$/.test(this.searchKeyword.trim())) {
                    params.sellerPhone = this.searchKeyword.trim();
                } else {
                    params.sellerId = this.searchKeyword.trim();
                }
            }
            
            const response = await CommissionApi.getWithdrawsForMonth(params);
            this.data = response;
            await this.loadAutoWithdrawJobStatus();
        } catch (error) {
            console.error("查询发佣数据失败:", error);
            this.showHintMessage("查询失败，请重试");
        } finally {
            this.loading = false;
        }
    }

    async loadAutoWithdrawJobStatus() {
        try {
            this.autoWithdrawJobStatus = await CommissionApi.getAutoWithdrawJobStatus();
        } catch (error) {
            console.error("查询自动提现任务状态失败:", error);
            const now = new Date();
            this.autoWithdrawJobStatus = {
                month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
                executedThisMonth: false,
                running: false,
            };
        }
    }

    async triggerAutoWithdrawJob() {
        if (this.autoWithdrawJobLoading) return;
        this.autoWithdrawJobLoading = true;
        try {
            const success = await CommissionApi.triggerAutoWithdrawJob();
            if (success) {
                this.showHintMessage("自动提现任务已触发");
                await this.loadAutoWithdrawJobStatus();
                await this.queryData();
            } else {
                this.showHintMessage("触发失败，请稍后重试");
            }
        } catch (error) {
            console.error("触发自动提现任务失败:", error);
            this.showHintMessage("触发失败，请稍后重试");
        } finally {
            this.autoWithdrawJobLoading = false;
        }
    }
    
    // 重置筛选条件
    resetFilters() {
        this.searchKeyword = "";
        this.statusFilter = "";
        this.initDefaultMonth();
    }
    
    // 标记单个为已发放
    async markOneAsPaid(withdrawId: string) {
        try {
            const success = await CommissionApi.markOneAsPaid(withdrawId);
            if (success) {
                this.showHintMessage("标记成功");
                // 重新加载数据
                await this.queryData();
            } else {
                this.showHintMessage("标记失败，请重试");
            }
        } catch (error) {
            console.error("标记发放失败:", error);
            this.showHintMessage("标记失败，请重试");
        }
    }
    
    // 标记所有为已发放
    async markAllAsPaid() {
        try {
            const success = await CommissionApi.markAllAsPaid();
            if (success) {
                this.showHintMessage("批量标记成功");
                // 重新加载数据
                await this.queryData();
            } else {
                this.showHintMessage("批量标记失败，请重试");
            }
        } catch (error) {
            console.error("批量标记发放失败:", error);
            this.showHintMessage("批量标记失败，请重试");
        }
    }
    
    // 显示确认弹窗
    showConfirmDialogForMarkAll() {
        this.confirmDialogTitle = "确认操作";
        this.confirmDialogContent = "确定要完成本月所有佣金的发放吗？此操作不可撤销。";
        this.confirmDialogAction = () => {
            this.markAllAsPaid();
            this.hideConfirmDialog();
        };
        this.showConfirmDialog = true;
    }

    showConfirmDialogForTriggerAutoWithdraw() {
        this.confirmDialogTitle = "确认操作";
        this.confirmDialogContent =
            "确定要手动触发自动提现任务吗？将为所有开启自动提现的卖家发起提现申请。";
        this.confirmDialogAction = () => {
            this.triggerAutoWithdrawJob();
            this.hideConfirmDialog();
        };
        this.showConfirmDialog = true;
    }
    
    // 隐藏确认弹窗
    hideConfirmDialog() {
        this.showConfirmDialog = false;
        this.confirmDialogTitle = "";
        this.confirmDialogContent = "";
        this.confirmDialogAction = null;
    }
    
    // 显示提示信息
    showHintMessage(message: string) {
        this.hintMessage = message;
        this.showHint = true;
    }
    
    // 隐藏提示信息
    hideHint() {
        this.showHint = false;
        this.hintMessage = "";
    }
    
    // 导出CSV文件
    async exportCsvFile() {
        if (!this.selectedMonth) return;
        
        try {
            const csvUrl = await CommissionApi.getAllWithdrawsCsvFileForMonth(this.selectedMonth);
            // 创建一个临时的a标签来下载文件
            const link = document.createElement('a');
            link.href = csvUrl;
            link.download = `佣金发放记录_${this.selectedMonth}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.showHintMessage("CSV文件导出成功");
        } catch (error) {
            console.error("导出CSV文件失败:", error);
            this.showHintMessage("导出失败，请重试");
        }
    }
    
    // 获取状态文本
    getStatusText(status: number): string {
        switch (status) {
            case 0: return "尚未发放";
            case 1: return "锁定中";
            case 2: return "已经发放";
            default: return "未知状态";
        }
    }
    
    // 获取状态颜色
    getStatusColor(status: number): string {
        switch (status) {
            case 0: return "#1890ff";
            case 1: return "#faad14";
            case 2: return "#52c41a";
            default: return "#d9d9d9";
        }
    }
    
    // 判断是否显示"完成佣金发放"按钮
    get shouldShowMarkAllButton(): boolean {
        return !this.searchKeyword.trim() && this.data !== null;
    }
    
    // 获取总结文案
    get summaryText(): string {
        if (!this.data) return "";
        
        if (this.searchKeyword.trim()) {
            return "搜索结果如下";
        } else {
            const month = this.selectedMonth.replace("-", "年") + "月";
            return `${month}总发放金额：${this.data.totalAmount.toLocaleString()}元，共${this.data.totalCount}人；已发放：${this.data.paidAmount.toLocaleString()}元，共${this.data.paidCount}人`;
        }
    }

    get autoWithdrawJobStatusText(): string {
        const status = this.autoWithdrawJobStatus;
        if (status?.running) return "自动提现任务：执行中…";
        if (status?.executedThisMonth) return "自动提现任务：本月已执行";
        return "自动提现任务：本月未执行";
    }

    get autoWithdrawJobStatusColor(): string {
        const status = this.autoWithdrawJobStatus;
        if (status?.running) return "processing";
        if (status?.executedThisMonth) return "success";
        return "warning";
    }
} 